import { useEffect, useState } from 'react';
import { isDateAfter, isDateValid } from 'utils/dateTimeUtils/dateValidationUtil';

import {
    MAX_PHOTOS,
    NOTES_MAX_LENGTH,
    PART_SERIAL_NUMBER_MAX_LENGTH
} from 'pages/MaintenancePage/components/LogServicePanel/constants';

import type {
    EditDraft,
    OnAddPhotos,
    OnRemovePhoto,
    ServiceRecord,
    ServiceRecordEdit,
    UseEditDraftParams,
    UseEditDraftReturn
} from './types';

// Seeds the edit form from the stored record. Tolerates null — the panel renders
// nothing without a record, but this hook still runs — by seeding an empty draft
// that can never build an edit.
const getDraftFromRecord = (record: ServiceRecord | null): EditDraft => ({
    notes: record?.notes ?? '',
    partSerialNumber:
        (record && record.eventType !== 'calibrated' ? record.partSerialNumber : undefined) ?? '',
    performedAt: record ? new Date(record.performedAt) : undefined,
    photos: record?.photos ?? []
});

const useEditDraft = ({ isOpen, record }: UseEditDraftParams): UseEditDraftReturn => {
    const [draft, setDraft] = useState<EditDraft>(() => getDraftFromRecord(record));

    // Reseed whenever the panel opens (or its target record changes) so reopening
    // never shows a stale draft.
    useEffect(() => {
        if (isOpen) {
            setDraft(getDraftFromRecord(record));
        }
    }, [isOpen, record]);

    const handleChangeNotes = ({ notes }: { notes: string }) =>
        setDraft(current => ({ ...current, notes }));

    // The field has no maxLength of its own, so this slice IS the length cap — the
    // same rule the log flow enforces.
    const handleChangePartSerialNumber = ({ partSerialNumber }: { partSerialNumber: string }) =>
        setDraft(current => ({
            ...current,
            partSerialNumber: partSerialNumber.slice(0, PART_SERIAL_NUMBER_MAX_LENGTH)
        }));

    const handleChangePerformedAt = ({ date }: { date?: Date }) =>
        setDraft(current => ({ ...current, performedAt: date }));

    // The field slices selections to the remaining slots; the cap here keeps the
    // draft honest even if a caller skips that guard.
    const handleAddPhotos: OnAddPhotos = ({ photos }) =>
        setDraft(current => ({
            ...current,
            photos: [...current.photos, ...photos].slice(0, MAX_PHOTOS)
        }));

    // NEVER revoke the removed photo's object URL: unlike the log draft, an edit's
    // photos may belong to the saved record, whose history thumbnails and change-log
    // entries must keep rendering whether the removal is cancelled or committed
    // (bounded prototype leak — same call the mutator's photo diff makes).
    const handleRemovePhoto: OnRemovePhoto = ({ photoId }) =>
        setDraft(current => ({
            ...current,
            photos: current.photos.filter(photo => photo.id !== photoId)
        }));

    const isPerformedAtValid =
        draft.performedAt !== undefined &&
        isDateValid(draft.performedAt) &&
        !isDateAfter(draft.performedAt, new Date());
    const areNotesValid = draft.notes.length <= NOTES_MAX_LENGTH;
    const isDraftValid = isPerformedAtValid && areNotesValid;

    // Values normalized like the log flow's buildDraft (trim-to-undefined, ISO date).
    // photos is always the full array — on an edit, undefined means "unchanged", so
    // an emptied list must still reach the mutator's diff as [].
    const buildEdit = (): ServiceRecordEdit | null => {
        if (!isDraftValid || !draft.performedAt) {
            return null;
        }

        return {
            notes: draft.notes.trim() || undefined,
            partSerialNumber: draft.partSerialNumber.trim() || undefined,
            performedAt: draft.performedAt.toISOString(),
            photos: draft.photos
        };
    };

    return {
        buildEdit,
        draft,
        handleAddPhotos,
        handleChangeNotes,
        handleChangePartSerialNumber,
        handleChangePerformedAt,
        handleRemovePhoto,
        isDraftValid
    };
};

export default useEditDraft;
